'use client'

import { useLoginByEmailMutation } from '@/graphql/auth/login.by.email.mutation'
import { useApiProxy } from '@/utils/use.api.proxy'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { captureException } from '@tucan/utils/error-tracking'
import { App, Button, Form, Input, Result, Spin } from 'antd'
import { signIn } from 'next-auth/react'
import { useRouter } from 'next/navigation'
import React, { useCallback, useEffect, useState } from 'react'
import { LoginWithGoogleButton } from './login.with.google.button'

interface LoginForm {
  email: string
  password?: string
}

export const Login = () => {
  const router = useRouter()
  const [form] = Form.useForm<LoginForm>()
  const [submitting, setSubmitting] = useState(false)
  const [storageAvailable, setStorageAvailable] = useState(true)
  const [challenge, setChallenge] = useState<string>()
  const { message } = App.useApp()

  const [loginByEmail] = useLoginByEmailMutation()
  const { trigger } = useApiProxy('/auth/password')

  const loginWithPassword = useCallback(
    async (e: LoginForm) => {
      setSubmitting(true)

      try {
        await trigger({
          data: {
            email: e.email,
            password: e.password,
          },
          method: 'POST',
        })
        void message.success('You are now logged in!')
        console.log('now redirect to root')
        router.refresh()
      } catch (e) {
        console.error(e)
        if (CombinedGraphQLErrors.is(e)) {
          e.errors.forEach((error) => {
            switch (error.message) {
              case 'ERROR_EMAIL':
                void message.error('There is something wrong with your password.')
                break
              case 'PASSWORD_EXPIRED':
                void message.error('Your password expired, please set a new one.')
                // TODO check redirect link!
                router.push(`/login/change-password?token=${error.extensions.originalError}`)
                break
            }
          })
        }
        setSubmitting(false)
      }
    },
    [message, router, trigger]
  )

  const loginWithEmail = useCallback(
    async (e: LoginForm) => {
      setSubmitting(true)

      const returnUrl = `${window.location.protocol}//${window.location.host}/login/pending?token=`

      try {
        const response = await loginByEmail({
          variables: {
            email: e.email,
            redirect: returnUrl,
            verifyWithPassword: !challenge,
          },
        })

        if (response.data?.status.message !== 'CHALLENGE_EMAIL') {
          setSubmitting(false)
          setChallenge(response.data.status.message)
          return
        }

        void message.success('E-Mail sent, check your inbox!')

        console.log('now redirect to pending page')
        router.push('/login/pending')
      } catch (e) {
        console.error(e)
        if (CombinedGraphQLErrors.is(e)) {
          e.errors.forEach((error) => {
            switch (error.message) {
              case 'ERROR_EMAIL':
                void message.error('Could not find user, do you want to register?')
                break

              case 'ERROR_REDIRECT':
                void message.error('Invalid Redirect Path')
                break
            }
          })
        }
      }
      setSubmitting(false)
    },
    [loginByEmail, challenge, message, router]
  )

  useEffect(() => {
    try {
      localStorage.setItem('test-field', 'test-value')
      localStorage.removeItem('test-field')
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStorageAvailable(true)
    } catch (e) {
      void message.error('No local storage available. Cannot login')
      captureException(e)
      setStorageAvailable(false)
    }
  }, [message])

  if (challenge) {
    switch (challenge) {
      case 'CHALLENGE_PASSWORD':
        return (
          <>
            <h1>Please enter your password</h1>
            <Spin spinning={submitting}>
              <Form
                form={form}
                onFinish={(data) => void loginWithPassword(data)}
                layout={'vertical'}
              >
                <Form.Item name="email" label={'Email'}>
                  <Input disabled={true} size={'large'} />
                </Form.Item>
                <Form.Item
                  name="password"
                  label={'Password'}
                  rules={[{ message: 'password required', required: true }]}
                >
                  <Input.Password size={'large'} autoFocus />
                </Form.Item>

                <Form.Item>
                  <Button
                    disabled={!storageAvailable}
                    size="large"
                    type="primary"
                    htmlType="submit"
                    block
                  >
                    Login
                  </Button>

                  <Button
                    size={'large'}
                    type={'link'}
                    block
                    onClick={() =>
                      void loginWithEmail({
                        email: form.getFieldValue(['email']),
                      })
                    }
                  >
                    Send me an email login link instead
                  </Button>
                </Form.Item>
              </Form>
            </Spin>
          </>
        )

      case 'CHALLENGE_ZITADEL':
        return void signIn(
          'zitadel',
          {},
          {
            login_hint: form.getFieldValue(['email']),
            prompt: 'login',
          }
        )
    }

    return (
      <Result
        status={'info'}
        title={`Unsupported challenge [${challenge}]`}
        extra={[
          <Button key={'reset'} onClick={() => setChallenge(undefined)}>
            Back to Login
          </Button>,
        ]}
      />
    )
  }

  return (
    <>
      <h1>Admin Login</h1>

      <Spin spinning={submitting}>
        <Form form={form} onFinish={(data) => void loginWithEmail(data)} layout={'vertical'}>
          <Form.Item
            name="email"
            rules={[
              {
                message: 'email required',
                required: true,
              },
              {
                message: 'not a valid email',
                type: 'email',
              },
            ]}
            label={'Email'}
          >
            <Input
              autoFocus
              disabled={!storageAvailable}
              placeholder={'please enter your email'}
              size={'large'}
            />
          </Form.Item>

          <Form.Item>
            <Button
              disabled={!storageAvailable}
              size="large"
              type="primary"
              htmlType="submit"
              block
            >
              Login
            </Button>
          </Form.Item>
          <Form.Item>
            <LoginWithGoogleButton
              disabled={!storageAvailable}
              onSubmitting={setSubmitting}
              onSuccess={() => {
                router.refresh()
              }}
            />
          </Form.Item>
        </Form>
      </Spin>
    </>
  )
}
